import {
  createFileRoute,
  defaultParseSearch,
  redirect,
  useNavigate,
  type NavigateOptions,
} from '@tanstack/react-router';
import { z } from 'zod';
import { Login } from '@/components/auth';
import { useAuth } from '@/contexts/AuthContext';
import { validateInternalUrl } from '@/lib/validateInternalUrl';
import { LoginRequest } from '@signalvote/shared';

const loginSearchSchema = z.object({
  redirect: z.string().optional(),
  campaignId: z.string().optional(),
});

/**
 * Split a validated internal href ("/path?x=1#h") into navigate/redirect
 * options. The router ignores `href` when following internal redirects
 * (buildLocation only reads to/search/hash), so passing `href` from
 * beforeLoad re-runs the guard forever and freezes the page.
 */
function toNavigateOptions(target: string): NavigateOptions {
  const url = new URL(target, window.location.origin);
  return {
    // Dynamic (runtime-validated) path, so it can't satisfy the literal route
    // union — the router resolves unknown paths through not-found handling.
    to: url.pathname as NavigateOptions['to'],
    search: defaultParseSearch(url.search),
    hash: url.hash.slice(1) || undefined,
  };
}

export const Route = createFileRoute('/login')({
  validateSearch: loginSearchSchema,
  beforeLoad: ({ context, search }) => {
    if (context.auth.user) {
      const target = validateInternalUrl(search.redirect) ?? '/app';
      throw redirect(toNavigateOptions(target));
    }
  },
  component: LoginRoute,
});

function LoginRoute() {
  const navigate = useNavigate();
  const { redirect: redirectTo } = Route.useSearch();
  const { login, loginLoading, loginError } = useAuth();

  const handleLogin = async (credentials: LoginRequest) => {
    try {
      await login(credentials);
      const target = validateInternalUrl(redirectTo) ?? '/app';
      navigate(toNavigateOptions(target));
    } catch {
      // Error is handled by AuthContext and passed via loginError
    }
  };

  return (
    <Login
      onLogin={handleLogin}
      isLoading={loginLoading}
      error={loginError}
    />
  );
}
